/**
 * Локальный каскад дат при перетаскивании на Ганте (логика согласована с backend cascade-update).
 */

export function addCalendarDays(date, days) {
  if (!date || days == null || Number.isNaN(days)) return date ? new Date(date.getTime()) : null;
  const d = new Date(date.getTime());
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d;
}

export function calendarSpanDays(start, end) {
  if (!start || !end) return 0;
  const a = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const b = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / 86400000));
}

export function getTaskBounds(task) {
  const s = task.actualStart || task.plannedStart;
  const e = task.actualEnd || task.plannedEnd;
  return { s, e };
}

function earliestStartFromLink(predStart, predEnd, linkType, lagDays, spanDays) {
  const lag = lagDays || 0;
  const lt = (linkType || 'FS').toUpperCase();
  if (lt === 'FS') return addCalendarDays(predEnd, lag + 1);
  if (lt === 'SS') return addCalendarDays(predStart, lag);
  if (lt === 'FF') return addCalendarDays(addCalendarDays(predEnd, lag), -spanDays);
  if (lt === 'SF') return addCalendarDays(addCalendarDays(predStart, lag), -spanDays);
  return addCalendarDays(predEnd, lag + 1);
}

function sameCalendarDay(a, b) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function cloneTaskDates(t) {
  return {
    ...t,
    plannedStart: t.plannedStart ? new Date(t.plannedStart.getTime()) : null,
    plannedEnd: t.plannedEnd ? new Date(t.plannedEnd.getTime()) : null,
    actualStart: t.actualStart ? new Date(t.actualStart.getTime()) : null,
    actualEnd: t.actualEnd ? new Date(t.actualEnd.getTime()) : null
  };
}

function recomputeSuccessor(succ, predEntries, byId) {
  const { s: sStart, e: sEnd } = getTaskBounds(succ);
  let spanDays;
  if (sStart) {
    spanDays = calendarSpanDays(sStart, sEnd || sStart);
  } else {
    spanDays = null;
    for (const p of predEntries) {
      const pred = byId.get(p.predecessor_id);
      if (!pred) continue;
      const { s: ps, e: pe } = getTaskBounds(pred);
      if (ps && pe) {
        spanDays = calendarSpanDays(ps, pe);
        break;
      }
    }
    if (spanDays == null) spanDays = 0;
  }

  const lower = [];
  for (const p of predEntries) {
    const pred = byId.get(p.predecessor_id);
    if (!pred) continue;
    const { s: ps, e: pe } = getTaskBounds(pred);
    if (!ps || !pe) continue;
    lower.push(earliestStartFromLink(ps, pe, p.link_type, p.lag_days, spanDays));
  }
  if (lower.length === 0) return null;
  const newStart = new Date(Math.max(...lower.map((d) => d.getTime())));
  const newEnd = addCalendarDays(newStart, spanDays);
  return { start: newStart, end: newEnd };
}

/**
 * После изменения rootTaskId пересчитывает actualStart/actualEnd для всех видимых последователей.
 * @param {Array} tasks — объекты с id, plannedStart/End, actualStart/End (Date)
 * @param {Array} edges — { source, target, link_type, lag_days }
 * @param {number} rootTaskId
 */
export function applyCascadeToTasks(tasks, edges, rootTaskId) {
  if (!tasks?.length || !edges?.length || rootTaskId == null) return tasks;

  const taskIds = new Set(tasks.map((t) => t.id));
  const byId = new Map();
  for (const t of tasks) {
    byId.set(t.id, cloneTaskDates(t));
  }

  const predecessorsMap = new Map();
  const successorsMap = new Map();
  for (const e of edges) {
    const sid = e.source;
    const tid = e.target;
    if (!taskIds.has(sid) || !taskIds.has(tid)) continue;
    if (!predecessorsMap.has(tid)) predecessorsMap.set(tid, []);
    predecessorsMap.get(tid).push({
      predecessor_id: sid,
      link_type: e.link_type,
      lag_days: e.lag_days
    });
    if (!successorsMap.has(sid)) successorsMap.set(sid, []);
    successorsMap.get(sid).push({ successor_id: tid });
  }

  const queue = [rootTaskId];
  while (queue.length) {
    const predId = queue.shift();
    const outs = successorsMap.get(predId);
    if (!outs) continue;
    for (const { successor_id } of outs) {
      const succ = byId.get(successor_id);
      if (!succ) continue;
      const preds = predecessorsMap.get(successor_id);
      if (!preds?.length) continue;
      const comp = recomputeSuccessor(succ, preds, byId);
      if (!comp) continue;
      const old = getTaskBounds(succ);
      if (sameCalendarDay(comp.start, old.s) && sameCalendarDay(comp.end, old.e)) continue;
      byId.set(successor_id, {
        ...succ,
        actualStart: comp.start,
        actualEnd: comp.end
      });
      queue.push(successor_id);
    }
  }

  return tasks.map((t) => byId.get(t.id) || t);
}
